// =====================================================================
// crmCustomer — Pilots: Metrics tab.
//
// Owns the metric framework definitions (PILOT_METRICS), the small
// aggregator helpers each metric's compute(pilots) uses, and the tab
// component itself. Loaded after pilots-shared.jsx so fmtPounds is in
// the global scope.
// =====================================================================

// Aggregator helpers used by the metric compute functions. Keeping them
// at module scope so each metric stays a one-liner.
function _mean(xs)   { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null; }
function _sum(xs)    { return xs.reduce((a, b) => a + b, 0); }
function _isClosed(p)    { return p.status === "Converted" || p.status === "Stopped"; }
function _isConverted(p) { return p.status === "Converted" || p.decisionAt90 === "convert"; }
function _reachedDay90(p) {
  if (p.decisionAt90 || p.status === "Converted" || p.status === "Stopped" || p.status === "AtDecision" || p.status === "Extension") return true;
  if (!p.day90Date) return false;
  return new Date(p.day90Date) <= new Date();
}
const NOT_TRACKED = { value: "—", sub: "Not captured in app yet" };

// Six categories × per-card content. The `crit` flag drives the Critical
// Few toggle; `key` is a stable identifier; `compute(pilots)` returns
// {value, sub} aggregated across the pilot cohort (or NOT_TRACKED when
// the underlying signal isn't captured in-app). Content adapted from
// docs/pilot-metrics-for-fundraising.
const PILOT_METRICS = [
  { key: "conv-day90", cat: "commercial", crit: true,
    label: "Commercial conversion", title: "Design-partner conversion at day 90",
    def: "With one client, conversion is binary and it is the headline. Does the design partner sign the annual starter at day 90, with the pilot fee credited?",
    capture: "Pilot row: day-90 decision, decision date, annual contract value, fee credited Y/N.",
    investor: "The clearest single signal that value becomes revenue.",
    target: "Convert to £12k annual + start the next logos",
    compute: (ps) => {
      const cohort = ps.filter(_reachedDay90);
      if (!cohort.length) return { value: "0 / 0", sub: "no pilots have reached day 90 yet" };
      const conv = cohort.filter(_isConverted).length;
      return { value: `${conv} / ${cohort.length}`, sub: `${Math.round(conv / cohort.length * 100)}% converted at day 90` };
    } },
  { key: "started-completed", cat: "commercial", crit: false,
    label: "Commercial conversion", title: "Pilots started vs completed",
    def: "How many pilots reached a defined end with a verdict, versus how many quietly stalled. Stalled pilots are a diagnostic, not a failure to hide.",
    capture: "Pilot status field (active / completed / stalled) with reason.",
    investor: "Completion rate shows whether your pilot is scoped to actually finish.",
    target: "> 70% reach a verdict",
    compute: (ps) => {
      if (!ps.length) return { value: "0 / 0", sub: "no pilots yet" };
      const done = ps.filter(_isClosed).length;
      return { value: `${done} / ${ps.length}`, sub: `${Math.round(done / ps.length * 100)}% reached a verdict` };
    } },
  { key: "first-to-start", cat: "commercial", crit: false,
    label: "Commercial conversion", title: "Time from first contact to pilot start",
    def: "Calendar days between qualified first conversation and the pilot going live. The leading half of your sales cycle.",
    capture: "CRM timestamps: first qualified call → pilot kickoff. Median, not mean, on small n.",
    investor: "Tells the story of friction in regulated buying.",
    target: "Trend it; direction beats a number",
    compute: () => NOT_TRACKED },
  { key: "value-uplift", cat: "value", crit: true,
    label: "Value delivered", title: "Maturity uplift vs baseline",
    def: "The movement in the customer's TRUST maturity score from day-zero baseline to pilot end. Your core proof metric.",
    capture: "Score each pilot at kickoff and at close. Store both. Note which pillar moved.",
    investor: "Converts a subjective maturity tool into a measurable, defensible outcome. This is the slide.",
    target: "≥ 1 level uplift in the launch pillar",
    compute: (ps) => {
      const scored = ps.filter(p => p.baselineScore != null && p.closeScore != null);
      if (!scored.length) return { value: "—", sub: "no pilot has both baseline and close scores yet" };
      const deltas = scored.map(p => p.closeScore - p.baselineScore);
      const m = _mean(deltas);
      const sign = m >= 0 ? "+" : "";
      const hitTarget = deltas.filter(d => d >= 1).length;
      return { value: `${sign}${m.toFixed(1)}`, sub: `mean delta · ${hitTarget}/${scored.length} hit ≥1 level` };
    } },
  { key: "value-time-cost", cat: "value", crit: true,
    label: "Value delivered", title: "Quantified time / cost saved",
    def: "Hard hours or cost removed from a named workflow. Tie it to a specific before/after the customer will attest to.",
    capture: "Ask the champion to state the old process time vs new, in writing, at pilot close.",
    investor: "Lets the buyer build their own ROI case. The basis of your pricing defence.",
    target: "A customer-attested number per pilot",
    compute: (ps) => {
      const hrs = ps.filter(p => p.timeSavedHours != null).map(p => p.timeSavedHours);
      const costs = ps.filter(p => p.costSavedPence != null).map(p => p.costSavedPence);
      if (!hrs.length && !costs.length) return { value: "—", sub: "no quantified savings captured yet" };
      const hrsTotal = _sum(hrs);
      const costTotal = _sum(costs);
      const hrsPart = hrs.length ? `${hrsTotal.toLocaleString()} hrs (n=${hrs.length})` : null;
      const costPart = costs.length ? `${fmtPounds(costTotal)} saved (n=${costs.length})` : null;
      return { value: [hrsPart, costPart].filter(Boolean).join(" · "), sub: "sum across pilots" };
    } },
  { key: "value-issues", cat: "value", crit: false,
    label: "Value delivered", title: "Issues / gaps surfaced and closed",
    def: "Threat-model findings, compliance clause gaps, or resilience weaknesses the tool surfaced, and how many the team actioned.",
    capture: "Product telemetry: findings generated, findings accepted/resolved.",
    investor: "Proves the output is real and acted on, not a report that gets filed.",
    target: "High action rate > high volume",
    compute: () => NOT_TRACKED },
  { key: "eng-ttfv", cat: "engagement", crit: true,
    label: "Engagement", title: "Time to first value (activation)",
    def: "Time from access granted to the first meaningful output the customer recognises as valuable.",
    capture: "Instrument the product. Timestamp account creation and the first 'aha' event.",
    investor: "Short TTFV predicts conversion and signals the product can scale beyond your own hand-holding.",
    target: "First value within session 1 or week 1",
    compute: () => NOT_TRACKED },
  { key: "eng-active", cat: "engagement", crit: false,
    label: "Engagement", title: "Active users / teams within the account",
    def: "Weekly active engineers, and how many distinct teams inside the org touched the product during the pilot.",
    capture: "Product analytics: WAU, distinct teams, sessions. Auto-captured.",
    investor: "Depth of use inside one logo is the leading indicator of expansion and retention.",
    target: "Use spreads beyond the champion",
    compute: () => NOT_TRACKED },
  { key: "eng-depth", cat: "engagement", crit: false,
    label: "Engagement", title: "Depth of feature use",
    def: "Which parts of the platform got used repeatedly versus opened once. Shows what your real wedge is.",
    capture: "Feature-level event tracking. Rank by repeat use across the pilot cohort.",
    investor: "Evidence you understand where value concentrates.",
    target: "A clear, repeated core workflow",
    compute: () => NOT_TRACKED },
  { key: "econ-acv", cat: "economics", crit: true,
    label: "Economics", title: "Validated price point / indicative ACV",
    def: "What the buyer actually agreed to pay, or signed an LOI against. The realised number per account.",
    capture: "Record quoted, negotiated, and final contract value for every pilot that converts.",
    investor: "Real ACV underpins every revenue projection. Investors stress-test this first.",
    target: "A defensible ACV from real signings",
    compute: (ps) => {
      const acvs = ps.filter(p => p.annualContractValuePence != null && p.annualContractValuePence > 0)
                     .map(p => p.annualContractValuePence);
      if (!acvs.length) return { value: "—", sub: "no signed ACVs captured yet" };
      const m = _mean(acvs);
      return { value: fmtPounds(m), sub: `mean across ${acvs.length} signed pilots` };
    } },
  { key: "econ-cycle", cat: "economics", crit: false,
    label: "Economics", title: "Sales cycle length",
    def: "Total elapsed time from first qualified contact to signed contract, including regulated-buyer overhead.",
    capture: "CRM stage timestamps. Report median and the spread.",
    investor: "Drives the capital-efficiency story and how much runway a seed round needs to buy.",
    target: "Know it honestly; long is fine if priced",
    compute: () => NOT_TRACKED },
  { key: "econ-cac", cat: "economics", crit: false,
    label: "Economics", title: "Cost / effort to win a pilot (CAC proxy)",
    def: "Founder hours and direct cost to land and run each pilot. At pre-seed this is mostly your time.",
    capture: "Rough time log per account: outreach, meetings, pilot support hours.",
    investor: "Early read on whether the motion can be repeated by someone other than the founder.",
    target: "Effort trending down per pilot",
    compute: () => NOT_TRACKED },
  { key: "proof-ref-champion", cat: "proof", crit: true,
    label: "Proof & references", title: "Reference depth & champion strength",
    def: "With one client, you trade breadth for depth. One articulate reference plus a champion who will defend the budget internally.",
    capture: "Secure reference consent and a verbatim testimonial at day 90.",
    investor: "A reference call with a credible design partner is where early diligence is won.",
    target: "1 deep reference + a budget-holding champion",
    compute: (ps) => {
      if (!ps.length) return { value: "0", sub: "no pilots yet" };
      const strong = ps.filter(p => p.referenceConsent && p.championContactId).length;
      return { value: `${strong} / ${ps.length}`, sub: "pilots with reference consent + named champion" };
    } },
  { key: "proof-buyer", cat: "proof", crit: false,
    label: "Proof & references", title: "Economic-buyer engagement",
    def: "Whether the CTO / Head of Engineering / VP Eng (who controls budget) was actually in the room.",
    capture: "Log buyer role and seniority per pilot. Flag user-only pilots.",
    investor: "Distinguishes a usage signal from a buying signal.",
    target: "Budget owner engaged before close",
    compute: (ps) => {
      if (!ps.length) return { value: "0 / 0", sub: "no pilots yet" };
      const with_ = ps.filter(p => p.economicBuyerContactId).length;
      return { value: `${with_} / ${ps.length}`, sub: `${Math.round(with_ / ps.length * 100)}% have a named economic buyer` };
    } },
  { key: "proof-verbatims", cat: "proof", crit: false,
    label: "Proof & references", title: "Qualitative verbatims",
    def: "Direct customer quotes on the problem and the value, captured verbatim.",
    capture: "Short structured close-out call per pilot. Record it.",
    investor: "Gives the deck and data-room narrative credibility.",
    target: "One sharp quote per pilot",
    compute: (ps) => {
      if (!ps.length) return { value: "0", sub: "no pilots yet" };
      const quoted = ps.filter(p => (p.referenceQuote || "").trim()).length;
      return { value: `${quoted} / ${ps.length}`, sub: "pilots with a verbatim quote captured" };
    } },
  { key: "momentum-expansion", cat: "momentum", crit: true,
    label: "Momentum & expansion", title: "Within-account expansion",
    def: "With one design partner, expansion is your strongest growth evidence. Track 3 → 5 projects, then to more teams or pillars.",
    capture: "Pilot row expansion field: projects now, projects to full starter.",
    investor: "Proxy for NRR before you have the data for a real NRR figure.",
    target: "3 → 5 projects, then beyond",
    compute: (ps) => {
      const converted = ps.filter(_isConverted);
      if (!converted.length) return { value: "—", sub: "no converted pilots yet" };
      const expanded = converted.filter(p => (p.expansionProjects || 0) >= 5).length;
      return { value: `${expanded} / ${converted.length}`, sub: "converted pilots at 5+ projects (full starter)" };
    } },
  { key: "momentum-renewal", cat: "momentum", crit: false,
    label: "Momentum & expansion", title: "Stated renewal intent",
    def: "Explicit, recorded intent to renew or continue beyond the pilot, captured before the pilot ends.",
    capture: "Direct ask in the close-out call: 'Will you continue, and at what scope?'",
    investor: "Leading indicator of retention.",
    target: "Intent captured for every pilot",
    compute: (ps) => {
      const converted = ps.filter(_isConverted).length;
      return { value: `${converted}`, sub: "pilots converted to the £12k starter (a hard renewal signal)" };
    } },
  { key: "momentum-next-logos", cat: "momentum", crit: false,
    label: "Momentum & expansion", title: "Sourcing the next logos",
    def: "Track the pipeline of additional clients you are building, and how each is sourced (referral from this design partner is strongest).",
    capture: "Simple pipeline: prospect, ICP fit, source, stage.",
    investor: "Turns a single-logo story into a repeatability story.",
    target: "2–3 more ICP logos in motion",
    compute: () => NOT_TRACKED },
];

const PILOT_CATEGORIES = [
  { key: "all",        label: "All" },
  { key: "commercial", label: "Commercial" },
  { key: "value",      label: "Value delivered" },
  { key: "engagement", label: "Engagement" },
  { key: "economics",  label: "Economics" },
  { key: "proof",      label: "Proof" },
  { key: "momentum",   label: "Momentum" },
];

// Dashboard-style view: KPI tiles only, no documentation text, no
// placeholder cards for metrics whose data isn't captured in-app. Each
// number reflects the live D.PILOTS slice — adding a pilot updates both
// the headline strip and the per-metric tiles on the next render.
function PilotsMetricsTab() {
  const [cat, setCat] = useState("all");
  const pilots = D.PILOTS || [];

  // Skip NOT_TRACKED metrics entirely — they're aspirational placeholders,
  // not data. They reappear naturally once a compute function returns
  // something other than NOT_TRACKED.
  const cards = PILOT_METRICS.filter(m => {
    if (cat !== "all" && m.cat !== cat) return false;
    const result = m.compute ? m.compute(pilots) : null;
    return result && result !== NOT_TRACKED && result.sub !== NOT_TRACKED.sub;
  });

  // Five-tile headline strip — top-of-page rollups across the whole
  // cohort. Reuses individual-metric compute functions where possible so
  // the numbers stay consistent with the tiles below.
  const convCard = PILOT_METRICS.find(m => m.key === "conv-day90");
  const signedAcv = _sum(pilots.filter(p => p.annualContractValuePence > 0).map(p => p.annualContractValuePence));
  const signedCount = pilots.filter(p => p.annualContractValuePence > 0).length;
  const scored = pilots.filter(p => p.baselineScore != null && p.closeScore != null);
  const meanDelta = scored.length ? _mean(scored.map(p => p.closeScore - p.baselineScore)) : null;
  const headline = [
    { label: "In flight",       value: pilots.filter(p => !_isClosed(p)).length, sub: `${pilots.length} total` },
    { label: "Paid",            value: pilots.filter(p => p.paid).length,        sub: `of ${pilots.length}` },
    { label: "Converted",       value: pilots.filter(_isConverted).length,       sub: convCard.compute(pilots).sub },
    { label: "Signed ACV",      value: fmtPounds(signedAcv),                     sub: `${signedCount} signed` },
    { label: "Maturity Δ (avg)", value: meanDelta == null ? "—" : (meanDelta >= 0 ? "+" : "") + meanDelta.toFixed(1), sub: `${scored.length} scored` },
  ];

  return (
    <>
      {/* Headline KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        {headline.map((h, i) => (
          <div key={i} className="card"><div className="card-body" style={{ padding: 12 }}>
            <div className="muted" style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{h.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.05 }}>{h.value}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{h.sub}</div>
          </div></div>
        ))}
      </div>

      {/* Category chips — narrow the grid by focus area */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {PILOT_CATEGORIES.map(c => (
          <button key={c.key}
                  className={"btn btn-sm" + (cat === c.key ? " btn-primary" : "")}
                  onClick={() => setCat(c.key)}>{c.label}</button>
        ))}
      </div>

      {/* KPI tile grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10, marginBottom: 24 }}>
        {cards.map((m) => {
          const result = m.compute(pilots);
          return (
            <div key={m.key} className="card" style={{
              borderTop: m.crit ? "2px solid var(--red, #b91c1c)" : undefined,
            }}>
              <div className="card-body" style={{ padding: 14 }}>
                <div className="muted" style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, lineHeight: 1.25 }}>{m.title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.05 }}>{result.value}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{result.sub}</div>
              </div>
            </div>
          );
        })}
        {cards.length === 0 && (
          <div className="muted" style={{ padding: 16 }}>No data for this filter.</div>
        )}
      </div>
    </>
  );
}

window.PILOT_METRICS    = PILOT_METRICS;
window.PILOT_CATEGORIES = PILOT_CATEGORIES;
window.PilotsMetricsTab = PilotsMetricsTab;
